import { handleError } from '@/utilities/errors/handleError';
import { ErrorType } from '@/utilities/errors/types';
import { useCallback, useRef } from 'react';
import { Logger } from '@/utilities/logger/datadog/rumEvents/rumEvents';
import { DdEventCategory, DdEventType } from '@/utilities/logger/datadog/types';
import { hashValue } from '@/utilities/helpers/hashValue';
import {
  HTML5_GEO_ACCESS_DENIED_ERROR_CODE,
  INVALID_SESSION_ERROR_CODE,
  locationErrorMessageMapper,
  locationErrorTypeMapper,
  MAX_TRANSIENT_803_AUTO_RETRIES,
  TRANSIENT_803_RETRY_DELAY_MS,
} from './constants';
import { requestGeolocationRelocate } from './request-geolocation-relocate';
import { shouldAutoRetryTransient803 } from './transient-geo-denied-retry';
import {
  resetGeolocationWithTriggerUserLocate,
  setGeolocationPassed,
  setGeolocationStatus,
} from '@/store/actions';
import { useStore } from '@/store/useStore';
import { updateGeolocationToken } from '@/api/geolocation/update-geolocation-token';
import {
  GeolocationError,
  GeolocationErrorType,
} from '@/store/slices/geolocation/types';
import { useGeolocationRefreshPopup } from './use-geolocation-refresh-popup';
import {
  processGeolocationDurationVital,
  stopVitalsOnError,
} from '@/utilities/logger/datadog/vitals';
import { VitalDurationType } from '@fanduel/casino-datadog-logger/src/types';
import { jwtDecode } from 'jwt-decode';
import { getGeolocationCookieName } from '@/utilities/getters/cookieGetters';
import extractGeolocationToken from '@/utilities/helpers/extractGeolocationToken';
import { setCookie } from '@/utilities/setters/cookieSetters';
import { clearSessionAndRedirectToLogin } from '@/utilities/helpers/clearSession';
import { usePopup } from '@/features/Popup/usePopup';
import { removeCookieEverywhere } from '@/utilities/helpers/removeCookieEverywhere';

type GeolocationFailure = {
  errorCode?: number;
  message?: string;
};

const hasActiveSession = () =>
  useStore.getState().userSessionContext.hasActiveSession;

const isGameLoaded = () => useStore.getState().gameStatus.gameLoadSuccess;

const isGeolocationSuccess = () =>
  useStore.getState().geolocationStatus.geolocationSuccess;

const isGameFetchSucess = () => useStore.getState().gameStatus.gameFetchSuccess;

const currentGeolocationToken = () =>
  useStore.getState().geolocationContext.geolocationToken;

const delayMs = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

const useGeolocationCallbackHandlers = () => {
  const { hidePopup } = usePopup();
  const { showRefreshing, showRefreshSuccess, showRefreshFailed } =
    useGeolocationRefreshPopup();
  const transient803AutoRetries = useRef(0);

  const handleGeolocationPassed = useCallback(
    (payload: { jwt?: string }) => {
      const { jwt: geolocationToken } = payload ?? {};
      if (!hasActiveSession() || !geolocationToken) return;

      // A token arriving after geolocation already succeeded is a mid-game
      // refresh: push it to FGP so the backend session stays valid. On the
      // first pass GLS init already carries the token, so no update is needed.

      const isRefresh = isGeolocationSuccess() && isGameFetchSucess();
      const hasTokenChanged = currentGeolocationToken() !== geolocationToken;

      setGeolocationPassed(geolocationToken);
      transient803AutoRetries.current = 0;

      const { expires }: { expires: string } = jwtDecode(geolocationToken);
      const expiryDate = new Date(expires);

      setCookie(
        getGeolocationCookieName(),
        geolocationToken,
        expiryDate.valueOf() - Date.now(),
      );

      if (isRefresh && hasTokenChanged) {
        updateGeolocationToken(geolocationToken);
      }

      processGeolocationDurationVital(VitalDurationType.STOP);

      if (isGameLoaded()) {
        showRefreshSuccess();
      }

      void hashValue(geolocationToken).then((geolocationTokenHash) => {
        Logger.logRumAction({
          name: '[GW][GEOLOCATION] Success',
          useCase: DdEventCategory.Geolocation,
          eventType: DdEventType.Success,
          context: {
            geolocationTokenHash,
          },
        });
      });
    },
    [showRefreshSuccess],
  );

  const handleGeolocationInProgress = useCallback(() => {
    if (!hasActiveSession()) return;

    if (
      useStore.getState().geolocationStatus.geolocationInProgress ||
      useStore.getState().geolocationContext.geolocationToken
    ) {
      return;
    }

    setGeolocationStatus({
      geolocationInProgress: true,
    });

    Logger.logRumAction({
      name: '[GW][GEOLOCATION] Progress',
      useCase: DdEventCategory.Geolocation,
      eventType: DdEventType.Info,
    });
  }, []);

  const applyGeolocationFailure = useCallback(
    (payload: { error?: GeolocationFailure }) => {
      const { error } = payload ?? {};
      const errorCode = error?.errorCode;

      let message = '';
      let geolocationErrorType;
      if (errorCode) {
        message = locationErrorMessageMapper[Number(errorCode)] ?? '';
        geolocationErrorType = locationErrorTypeMapper[Number(errorCode)];
      }

      handleError<GeolocationError>(
        {
          type: ErrorType.GEOLOCATION as ErrorType.GEOLOCATION,
          payload: {
            type: geolocationErrorType,
            message,
            errorCode,
          },
        },
        { skipLogging: true },
      );

      // A mid-game refresh failure surfaces the popup's ERROR step and must NOT
      // also drop to the full-screen error screen; only a first-time failure
      // does. showRefreshFailed() returns whether it handled a refresh in
      // progress; it's only attempted once the game is loaded.
      const shownAsRefresh = isGameLoaded() && showRefreshFailed();
      if (!shownAsRefresh) {
        setGeolocationStatus({
          geolocationFailed: true,
        });
      }

      Logger.logRumError({
        name: '[GW][GEOLOCATION] Failure',
        useCase: DdEventCategory.Geolocation,
        eventType: DdEventType.Error,
        stack: error instanceof Error ? error.stack : undefined,
        context: {
          message,
          errorCode,
        },
      });

      stopVitalsOnError({
        errorCode: errorCode?.toString() ?? '',
        errorMessage: message,
        errorType: geolocationErrorType,
      });
      processGeolocationDurationVital(VitalDurationType.STOP);
    },
    [showRefreshFailed, hidePopup],
  );

  const handleGeolocationFailed = useCallback(
    (payload: { error?: GeolocationFailure }) => {
      if (!hasActiveSession()) return;

      const { error } = payload ?? {};

      if (error?.errorCode === INVALID_SESSION_ERROR_CODE) {
        void clearSessionAndRedirectToLogin(undefined, hidePopup, true);
        return;
      }

      const errorCode = error?.errorCode;

      if (
        errorCode === HTML5_GEO_ACCESS_DENIED_ERROR_CODE &&
        !isGameLoaded() &&
        transient803AutoRetries.current < MAX_TRANSIENT_803_AUTO_RETRIES
      ) {
        setGeolocationStatus({
          geolocationFailed: false,
          geolocationRejected: false,
          geolocationInProgress: true,
        });

        void (async () => {
          if (!(await shouldAutoRetryTransient803())) {
            applyGeolocationFailure(payload);
            return;
          }

          transient803AutoRetries.current += 1;

          Logger.logRumAction({
            name: '[GW][GEOLOCATION] Transient 803 auto-retry',
            useCase: DdEventCategory.Geolocation,
            eventType: DdEventType.Info,
            context: { errorCode },
          });

          await delayMs(TRANSIENT_803_RETRY_DELAY_MS);

          if (!hasActiveSession()) return;

          resetGeolocationWithTriggerUserLocate();
          setGeolocationStatus({ geolocationInProgress: true });
          requestGeolocationRelocate({ force: true });
        })();
        return;
      }

      applyGeolocationFailure(payload);
    },
    [applyGeolocationFailure, hidePopup],
  );

  const handleGeolocationRejected = useCallback(
    (action) => {
      if (!hasActiveSession()) return;

      const message = action.rejection.message;
      const { troubleshooters } = action.rejection ?? {};

      handleError<GeolocationError>(
        {
          type: ErrorType.GEOLOCATION as ErrorType.GEOLOCATION,
          payload: {
            type: GeolocationErrorType.Validation,
            message,
            troubleshooters,
          },
        },
        { skipLogging: true },
      );

      // A mid-game refresh rejection surfaces the popup's ERROR step and must
      // NOT also drop to the full-screen rejected screen; only a first-time
      // rejection does (mirrors handleGeolocationFailed).
      const shownAsRefresh = isGameLoaded() && showRefreshFailed();
      if (!shownAsRefresh) {
        setGeolocationStatus({
          geolocationRejected: true,
        });
      }

      Logger.logRumError({
        name: '[GW][GEOLOCATION] Failure',
        useCase: DdEventCategory.Geolocation,
        eventType: DdEventType.Error,
        context: {
          message,
          troubleshooters,
        },
      });

      stopVitalsOnError({
        errorMessage: message,
        errorType: GeolocationErrorType.Validation,
      });
      processGeolocationDurationVital(VitalDurationType.STOP);
    },
    [showRefreshFailed],
  );

  const handleGeolocationInvalidated = useCallback(
    (action) => {
      removeCookieEverywhere(getGeolocationCookieName());

      if (!hasActiveSession()) return;

      const message = action.rejection?.message ?? '';

      // Mid-game geo invalidation: pause the game behind the refresh popup
      // (LOADING step) rather than dropping straight to the failure screen.
      // The success/error step follows once the re-check resolves.
      if (isGameLoaded()) {
        showRefreshing();
      }

      handleError<GeolocationError>(
        {
          type: ErrorType.GEOLOCATION as ErrorType.GEOLOCATION,
          payload: {
            type: GeolocationErrorType.Validation,
            message,
          },
        },
        { skipLogging: true },
      );

      Logger.logRumError({
        name: '[GW][GEOLOCATION] Invalidated',
        useCase: DdEventCategory.Geolocation,
        eventType: DdEventType.Error,
        context: {
          message,
        },
      });
    },
    [showRefreshing],
  );

  const handleXsellGeolocationUpdate = useCallback(() => {
    if (!hasActiveSession()) return;

    const geolocationToken = extractGeolocationToken();
    if (!geolocationToken) return;

    handleGeolocationPassed({ jwt: geolocationToken });
  }, [handleGeolocationPassed]);

  return {
    handleGeolocationPassed,
    handleGeolocationInProgress,
    handleGeolocationFailed,
    handleGeolocationRejected,
    handleGeolocationInvalidated,
    handleXsellGeolocationUpdate,
  };
};

export default useGeolocationCallbackHandlers;
